import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  CheckboxGroup,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import { FiPlus, FiSave, FiTrash2 } from 'react-icons/fi'
import type {
  SupportFormAttachmentPolicy,
  SupportFormCategory,
  SupportFormSubcategory,
  SupportFormSubtopic,
} from '@/types'
import { formatDateTime } from '@/utils/formatUtils'
import {
  FILE_TYPE_OPTIONS,
  applyDefaultAttachmentTypes,
  buildAllowedMimeTypes,
  createEmptySubtopic,
  ensureArray,
  normalizePolicy,
  normalizeSubtopicType,
  parsePositiveInt,
  slugify,
  type TemplateDefaultsState,
} from './supportPageHelpers'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { DropdownSelect } from '@/components/common/DropdownSelect'

interface SupportTemplateTabProps {
  t: TFunction
  cardBg: string
  subtleBorder: string
  mutedBg: string
  subtopicBg: string
  templateDirty: boolean
  isFetchingTemplate: boolean
  isSavingTemplate: boolean
  isTemplateLoading: boolean
  templateError: boolean
  templateSlug: string
  templateName: string
  templateVersion: string
  templateDefaults: TemplateDefaultsState
  templateCategories: SupportFormCategory[]
  templateUpdatedAt?: string | null
  onRefreshTemplate: () => void
  onSaveTemplate: () => void
  onTemplateSlugChange: (value: string) => void
  onTemplateNameChange: (value: string) => void
  onTemplateVersionChange: (value: string) => void
  onTemplateDefaultsChange: (next: TemplateDefaultsState) => void
  updateCategory: (
    index: number,
    updater: (category: SupportFormCategory) => SupportFormCategory
  ) => void
  updateSubcategory: (
    categoryIndex: number,
    subIndex: number,
    updater: (subcategory: SupportFormSubcategory) => SupportFormSubcategory
  ) => void
  updateSubtopic: (
    categoryIndex: number,
    subIndex: number,
    topicIndex: number,
    updater: (subtopic: SupportFormSubtopic) => SupportFormSubtopic
  ) => void
  addCategory: () => void
  removeCategory: (index: number) => void
  addSubcategory: (categoryIndex: number) => void
  removeSubcategory: (categoryIndex: number, subIndex: number) => void
  addSubtopic: (categoryIndex: number, subIndex: number) => void
  removeSubtopic: (categoryIndex: number, subIndex: number, topicIndex: number) => void
  markTemplateDirty: () => void
}

export function SupportTemplateTab({
  t,
  cardBg,
  subtleBorder,
  mutedBg,
  subtopicBg,
  templateDirty,
  isFetchingTemplate,
  isSavingTemplate,
  isTemplateLoading,
  templateError,
  templateSlug,
  templateName,
  templateVersion,
  templateDefaults,
  templateCategories,
  templateUpdatedAt,
  onRefreshTemplate,
  onSaveTemplate,
  onTemplateSlugChange,
  onTemplateNameChange,
  onTemplateVersionChange,
  onTemplateDefaultsChange,
  updateCategory,
  updateSubcategory,
  updateSubtopic,
  addCategory,
  removeCategory,
  addSubcategory,
  removeSubcategory,
  addSubtopic,
  removeSubtopic,
  markTemplateDirty,
}: SupportTemplateTabProps) {
  const renderAttachmentEditor = (
    policy: SupportFormAttachmentPolicy | undefined,
    onChange: (next: SupportFormAttachmentPolicy) => void
  ) => {
    const currentPolicy: SupportFormAttachmentPolicy = {
      enabled: false,
      allowedExtensions: [],
      allowedMimeTypes: [],
      ...normalizePolicy(policy),
    }

    return (
      <Box borderWidth="1px" borderColor={subtleBorder} borderRadius="md" p={2} mt={2}>
        <HStack
          justify="space-between"
          align={{ base: 'stretch', sm: 'center' }}
          mb={currentPolicy.enabled ? 2 : 0}
          flexWrap="wrap"
          gap={2}
        >
          <Text fontSize="sm" fontWeight="semibold">
            {t('support.template.fields.attachmentsTitle')}
          </Text>
          <HStack spacing={2} justify={{ base: 'space-between', sm: 'flex-start' }}>
            <Text fontSize="sm" color="gray.500">
              {t('support.template.fields.attachmentsEnabled')}
            </Text>
            <Switch
              size="sm"
              isChecked={currentPolicy.enabled}
              onChange={(event) => {
                onChange(
                  applyDefaultAttachmentTypes({
                    ...currentPolicy,
                    enabled: event.target.checked,
                  })
                )
                markTemplateDirty()
              }}
            />
          </HStack>
        </HStack>

        {currentPolicy.enabled && (
          <VStack spacing={2} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">
                {t('support.template.fields.attachmentsMaxFiles')}
              </FormLabel>
              <Input
                size="xs"
                type="number"
                min={1}
                step={1}
                value={currentPolicy.maxFiles ?? ''}
                onChange={(event) => {
                  const nextValue = event.target.value
                  const nextMaxFiles = nextValue ? parsePositiveInt(nextValue) : null
                  onChange({
                    ...currentPolicy,
                    maxFiles: nextMaxFiles ?? undefined,
                  })
                  markTemplateDirty()
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs">
                {t('support.template.fields.attachmentsTypes')}
              </FormLabel>
              <CheckboxGroup
                value={currentPolicy.allowedExtensions}
                onChange={(values) => {
                  const nextExtensions = (values as string[]) ?? []
                  onChange({
                    ...currentPolicy,
                    enabled: true,
                    allowedExtensions: nextExtensions,
                    allowedMimeTypes: buildAllowedMimeTypes(
                      nextExtensions,
                      ensureArray(currentPolicy.allowedMimeTypes)
                    ),
                  })
                  markTemplateDirty()
                }}
              >
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={1}>
                  {FILE_TYPE_OPTIONS.map((option) => (
                    <Checkbox key={option.extension} value={option.extension} size="sm" spacing={1}>
                      <Text fontSize="xs">{option.label}</Text>
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
            </FormControl>
          </VStack>
        )}
      </Box>
    )
  }

  return (
    <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
      <CardHeader px={3} pt={3} pb={2}>
        <HStack justify="space-between" align="start" spacing={4} flexWrap="wrap" gap={2}>
          <Box flex={{ base: '1 1 100%', sm: '1' }} minW={0}>
            <Heading size="sm">{t('support.template.title')}</Heading>
            <Text color="gray.500" fontSize="xs" mt={1}>
              {t('support.template.subtitle')}
            </Text>
          </Box>
          <HStack
            spacing={2}
            flexWrap="wrap"
            justify={{ base: 'flex-start', sm: 'flex-end' }}
            w={{ base: 'full', sm: 'auto' }}
          >
            {templateDirty && (
              <Badge colorScheme="yellow" variant="subtle">
                {t('support.template.unsaved')}
              </Badge>
            )}
            <RefreshIconButton
              label={t('common.refresh')}
              size="xs"
              onClick={onRefreshTemplate}
              isLoading={isFetchingTemplate}
            />
            <Button
              size="xs"
              colorScheme="blue"
              leftIcon={<FiSave />}
              onClick={onSaveTemplate}
              isLoading={isSavingTemplate}
              isDisabled={isTemplateLoading || templateError}
            >
              {t('common.save')}
            </Button>
          </HStack>
        </HStack>
      </CardHeader>
      <CardBody px={3} pt={2} pb={3}>
        {templateError && (
          <Text color="red.500" mb={2} fontSize="sm">
            {t('support.template.loadFailed')}
          </Text>
        )}
        <VStack spacing={3} align="stretch">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
            <FormControl>
              <FormLabel>{t('support.template.fields.slug')}</FormLabel>
              <Input
                size="xs"
                value={templateSlug}
                onChange={(event) => onTemplateSlugChange(event.target.value)}
                placeholder="default"
              />
            </FormControl>
            <FormControl>
              <FormLabel>{t('support.template.fields.name')}</FormLabel>
              <Input
                size="xs"
                value={templateName}
                onChange={(event) => onTemplateNameChange(event.target.value)}
                placeholder="Default support ticket"
              />
            </FormControl>
            <FormControl>
              <FormLabel>{t('support.template.fields.version')}</FormLabel>
              <Input
                size="xs"
                value={templateVersion}
                onChange={(event) => onTemplateVersionChange(event.target.value)}
                placeholder={t('support.template.fields.versionPlaceholder')}
              />
            </FormControl>
          </SimpleGrid>

          <Divider my={2} />

          <Box>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>
              {t('support.template.sections.defaults')}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
              <FormControl>
                <FormLabel>{t('support.template.fields.defaultsMessageLength')}</FormLabel>
                <Input
                  size="xs"
                  type="number"
                  min={1}
                  step={1}
                  value={templateDefaults.maxMessageLength}
                  onChange={(event) =>
                    onTemplateDefaultsChange({
                      ...templateDefaults,
                      maxMessageLength: event.target.value,
                    })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('support.template.fields.defaultsAttachmentSize')}</FormLabel>
                <Input
                  size="xs"
                  type="number"
                  min={0.1}
                  step={0.1}
                  value={templateDefaults.maxAttachmentSizeMb}
                  onChange={(event) =>
                    onTemplateDefaultsChange({
                      ...templateDefaults,
                      maxAttachmentSizeMb: event.target.value,
                    })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('support.template.fields.defaultsAttachmentCount')}</FormLabel>
                <Input
                  size="xs"
                  type="number"
                  min={1}
                  step={1}
                  value={templateDefaults.maxAttachments}
                  onChange={(event) =>
                    onTemplateDefaultsChange({
                      ...templateDefaults,
                      maxAttachments: event.target.value,
                    })
                  }
                />
              </FormControl>
            </SimpleGrid>
          </Box>

          <Divider my={2} />

          <Box>
            <HStack justify="space-between" align="center" mb={2} flexWrap="wrap" gap={2}>
              <Text fontSize="sm" fontWeight="semibold">
                {t('support.template.sections.categories')}
              </Text>
              <Button size="xs" variant="outline" leftIcon={<FiPlus />} onClick={addCategory}>
                {t('support.template.fields.addCategory')}
              </Button>
            </HStack>

            {templateCategories.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                {t('support.template.emptyCategories')}
              </Text>
            ) : (
              <VStack spacing={2} align="stretch">
                {templateCategories.map((category, categoryIndex) => (
                  <Box
                    key={`${category.id}-${categoryIndex}`}
                    borderWidth="1px"
                    borderColor={subtleBorder}
                    borderRadius="lg"
                    p={2}
                  >
                    <HStack justify="space-between" align="center" mb={2} flexWrap="wrap" gap={2}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {t('support.template.fields.categoryTitle')} {categoryIndex + 1}
                      </Text>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        aria-label={t('support.template.fields.remove')}
                        icon={<FiTrash2 />}
                        onClick={() => removeCategory(categoryIndex)}
                      />
                    </HStack>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                      <FormControl>
                        <FormLabel>{t('support.template.fields.categoryLabel')}</FormLabel>
                        <Input
                          size="xs"
                          value={category.label}
                          onChange={(event) => {
                            const nextLabel = event.target.value
                            updateCategory(categoryIndex, (current) => ({
                              ...current,
                              label: nextLabel,
                              id: current.id ? current.id : slugify(nextLabel),
                            }))
                          }}
                          placeholder={t('support.template.fields.labelPlaceholder')}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>{t('support.template.fields.categoryId')}</FormLabel>
                        <Input
                          size="xs"
                          value={category.id}
                          onChange={(event) => {
                            updateCategory(categoryIndex, (current) => ({
                              ...current,
                              id: event.target.value,
                            }))
                          }}
                          placeholder={t('support.template.fields.idPlaceholder')}
                        />
                      </FormControl>
                    </SimpleGrid>

                    <Divider my={2} />

                    <HStack justify="space-between" align="center" mb={2} flexWrap="wrap" gap={2}>
                      <Text fontSize="xs" fontWeight="semibold">
                        {t('support.template.fields.subcategoryTitle')}
                      </Text>
                      <Button
                        size="xs"
                        variant="outline"
                        leftIcon={<FiPlus />}
                        onClick={() => addSubcategory(categoryIndex)}
                      >
                        {t('support.template.fields.addSubcategory')}
                      </Button>
                    </HStack>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                      {category.subcategories.map((subcategory, subIndex) => {
                        const subtopics = ensureArray(subcategory.subtopics)
                        const subtopicType = normalizeSubtopicType(
                          subcategory.subtopicType,
                          subtopics.length
                        )
                        const hasSubtopics = Boolean(subtopicType)
                        const canAddSubtopic =
                          subtopicType === 'options' ||
                          (subtopicType === 'open' && subtopics.length === 0)

                        return (
                          <Box
                            key={`${subcategory.id}-${subIndex}`}
                            borderWidth="1px"
                            borderColor={subtleBorder}
                            borderRadius="md"
                            bg={mutedBg}
                            p={2}
                          >
                            <HStack justify="space-between" align="center" mb={2} flexWrap="wrap" gap={2}>
                              <Text fontSize="sm" fontWeight="semibold">
                                {t('support.template.fields.subcategoryTitle')} {subIndex + 1}
                              </Text>
                              <IconButton
                                size="xs"
                                variant="ghost"
                                aria-label={t('support.template.fields.remove')}
                                icon={<FiTrash2 />}
                                onClick={() => removeSubcategory(categoryIndex, subIndex)}
                              />
                            </HStack>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                              <FormControl>
                                <FormLabel fontSize="sm">
                                  {t('support.template.fields.subcategoryLabel')}
                                </FormLabel>
                                <Input
                                  size="xs"
                                  value={subcategory.label}
                                  onChange={(event) => {
                                    const nextLabel = event.target.value
                                    updateSubcategory(categoryIndex, subIndex, (current) => ({
                                      ...current,
                                      label: nextLabel,
                                      id: current.id ? current.id : slugify(nextLabel),
                                    }))
                                  }}
                                  placeholder={t('support.template.fields.labelPlaceholder')}
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="sm">
                                  {t('support.template.fields.subcategoryId')}
                                </FormLabel>
                                <Input
                                  size="xs"
                                  value={subcategory.id}
                                  onChange={(event) => {
                                    updateSubcategory(categoryIndex, subIndex, (current) => ({
                                      ...current,
                                      id: event.target.value,
                                    }))
                                  }}
                                  placeholder={t('support.template.fields.idPlaceholder')}
                                />
                              </FormControl>
                            </SimpleGrid>

                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} mt={2}>
                              <FormControl>
                                <FormLabel fontSize="sm">
                                  {t('support.template.fields.subtopicType')}
                                </FormLabel>
                                <DropdownSelect
                                  size="xs"
                                  value={subtopicType ?? ''}
                                  options={[
                                    {
                                      value: '',
                                      label: t('support.template.fields.subtopicTypeNone'),
                                    },
                                    {
                                      value: 'options',
                                      label: t('support.template.fields.subtopicTypeOptions'),
                                    },
                                    {
                                      value: 'open',
                                      label: t('support.template.fields.subtopicTypeOpen'),
                                    },
                                  ]}
                                  onChange={nextValue => {
                                    const nextType =
                                      nextValue === 'options' || nextValue === 'open'
                                        ? nextValue
                                        : undefined
                                    updateSubcategory(categoryIndex, subIndex, (current) => {
                                      let nextSubtopics = ensureArray(current.subtopics)
                                      if (!nextType) {
                                        nextSubtopics = []
                                      } else if (nextType === 'open') {
                                        if (nextSubtopics.length === 0) {
                                          nextSubtopics = [createEmptySubtopic()]
                                        } else {
                                          nextSubtopics = [nextSubtopics[0]]
                                        }
                                      } else if (
                                        nextType === 'options' &&
                                        nextSubtopics.length === 0
                                      ) {
                                        nextSubtopics = [createEmptySubtopic()]
                                      }
                                      return {
                                        ...current,
                                        subtopicType: nextType,
                                        subtopics: nextSubtopics,
                                        attachmentPolicy: nextType
                                          ? undefined
                                          : current.attachmentPolicy,
                                      }
                                    })
                                  }}
                                  ariaLabel={t('support.template.fields.subtopicType')}
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="sm">
                                  {t('support.template.fields.subtopicTitleLabel')}
                                </FormLabel>
                                <Input
                                  size="xs"
                                  value={subcategory.subtopicTitle ?? ''}
                                  isDisabled={!subtopicType}
                                  onChange={(event) => {
                                    updateSubcategory(categoryIndex, subIndex, (current) => ({
                                      ...current,
                                      subtopicTitle: event.target.value,
                                    }))
                                  }}
                                  placeholder={t('support.template.fields.subtopicTitlePlaceholder')}
                                />
                              </FormControl>
                            </SimpleGrid>

                            {!hasSubtopics &&
                              renderAttachmentEditor(subcategory.attachmentPolicy, (nextPolicy) => {
                                updateSubcategory(categoryIndex, subIndex, (current) => ({
                                  ...current,
                                  attachmentPolicy: nextPolicy,
                                }))
                              })}

                            <Divider my={2} />

                            <HStack justify="space-between" align="center" mb={1} flexWrap="wrap" gap={2}>
                              <Text fontSize="sm" fontWeight="semibold">
                                {t('support.template.fields.subtopicTitle')}
                              </Text>
                              <Button
                                size="xs"
                                variant="ghost"
                                leftIcon={<FiPlus />}
                                isDisabled={!canAddSubtopic}
                                onClick={() => addSubtopic(categoryIndex, subIndex)}
                              >
                                {t('support.template.fields.addSubtopic')}
                              </Button>
                            </HStack>

                            {!hasSubtopics ? (
                              <Text fontSize="sm" color="gray.500">
                                {t('support.template.emptySubtopics')}
                              </Text>
                            ) : (
                              <VStack spacing={2} align="stretch">
                                {subtopics.map((subtopic, topicIndex) => (
                                  <Box
                                    key={`${subtopic.id}-${topicIndex}`}
                                    borderWidth="1px"
                                    borderStyle="dashed"
                                    borderColor={subtleBorder}
                                    borderRadius="md"
                                    bg={subtopicBg}
                                    p={2}
                                  >
                                    <HStack justify="space-between" align="center" mb={2} flexWrap="wrap" gap={2}>
                                      <Text fontSize="sm" fontWeight="semibold">
                                        {t('support.template.fields.subtopicTitle')} {topicIndex + 1}
                                      </Text>
                                      <IconButton
                                        size="xs"
                                        variant="ghost"
                                        aria-label={t('support.template.fields.remove')}
                                        icon={<FiTrash2 />}
                                        onClick={() =>
                                          removeSubtopic(categoryIndex, subIndex, topicIndex)
                                        }
                                      />
                                    </HStack>
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                      <FormControl>
                                        <FormLabel fontSize="sm">
                                          {t('support.template.fields.subtopicLabel')}
                                        </FormLabel>
                                        <Input
                                          size="xs"
                                          value={subtopic.label}
                                          onChange={(event) => {
                                            const nextLabel = event.target.value
                                            updateSubtopic(
                                              categoryIndex,
                                              subIndex,
                                              topicIndex,
                                              (current) => ({
                                                ...current,
                                                label: nextLabel,
                                                id: current.id
                                                  ? current.id
                                                  : slugify(nextLabel),
                                              })
                                            )
                                          }}
                                          placeholder={t('support.template.fields.labelPlaceholder')}
                                        />
                                      </FormControl>
                                      <FormControl>
                                        <FormLabel fontSize="sm">
                                          {t('support.template.fields.subtopicId')}
                                        </FormLabel>
                                        <Input
                                          size="xs"
                                          value={subtopic.id}
                                          onChange={(event) => {
                                            updateSubtopic(
                                              categoryIndex,
                                              subIndex,
                                              topicIndex,
                                              (current) => ({
                                                ...current,
                                                id: event.target.value,
                                              })
                                            )
                                          }}
                                          placeholder={t('support.template.fields.idPlaceholder')}
                                        />
                                      </FormControl>
                                    </SimpleGrid>

                                    {renderAttachmentEditor(subtopic.attachmentPolicy, (nextPolicy) => {
                                      updateSubtopic(
                                        categoryIndex,
                                        subIndex,
                                        topicIndex,
                                        (current) => ({
                                          ...current,
                                          attachmentPolicy: nextPolicy,
                                        })
                                      )
                                    })}
                                  </Box>
                                ))}
                              </VStack>
                            )}
                          </Box>
                        )
                      })}
                    </SimpleGrid>
                  </Box>
                ))}
              </VStack>
            )}
          </Box>

          <HStack justify="space-between">
            <Text fontSize="xs" color="gray.500">
              {templateUpdatedAt
                ? t('support.template.updatedAt', {
                    date: formatDateTime(templateUpdatedAt),
                  })
                : t('support.template.updatedAtEmpty')}
            </Text>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  )
}
